/**
 * `/databases` — the map, and `/databases/<engine>/<ns>/<name>` — one cluster.
 *
 * ---------------------------------------------------------------------------
 * THE MAP IS THE POINT
 * ---------------------------------------------------------------------------
 * PLAN §7 asks for "the map: each cluster → instances → node → role". The
 * previous page rendered eleven equal-weight cards in engine order, which
 * answers "what clusters exist" — a question nobody has — and buries the one
 * that gets asked at 03:00: **if db-fsn-1 is gone, what did I just lose?**
 *
 * So the page leads with placement BY NODE. Every instance the API reported,
 * grouped under the node it sits on, role-tagged, with primaries called out.
 * The grouping is derived from `cluster.instances[].node` and nothing else.
 *
 * Instances whose node the API did not report are grouped under their own
 * dotted heading rather than dropped or lumped in with a real node. "We do not
 * know where this replica is" is a materially different statement from "this
 * replica is on db-hel-1", and a map that silently omits the first is a map you
 * cannot plan a failover from.
 *
 * ---------------------------------------------------------------------------
 * PROXYSQL
 * ---------------------------------------------------------------------------
 * The ProxySQL row is the load-bearing one: it renders `blocked`, with its own
 * dashed container and its own icon, and with the recorded reason ("no exporter
 * sidecar (#356, D8)") in the body. It is never green, never omitted, and never
 * quietly folded into "empty".
 */

import type { ReactNode } from 'react';

import { humanizeSeconds, relativeTime } from '../lib/format';
import { formatNumber, labelFor } from '../lib/signal';
import type { Signal } from '../lib/signal';
import {
  Absent,
  Card,
  Eyebrow,
  Ident,
  KeyValue,
  Placeholder,
  SignalPanel,
  SignalValue,
  Skeleton,
  StatusPill,
} from '../components/primitives';
import { SeriesChart } from '../components/SeriesChart';

interface DbInstance {
  name: string;
  node: string | null;
  role: string | null;
  ready: boolean | null;
  lagSeconds: Signal<number>;
  backends: Signal<number>;
  restarts?: number | null;
}

interface DbCluster {
  engine: string;
  namespace: string;
  name: string;
  outcome: string;
  outcomeReason: string | null;
  ready: boolean | null;
  primary: string | null;
  instances: DbInstance[];
  archiveFailures: Signal<number>;
  lastBackup: Signal<{ at: string | null; ageHours: number | null; reason: string }>;
  restoreDrill: Signal<number>;
  backends: Signal<{ name: string; state: string; health: string }[]>;
  notes: string[];
}

export interface DatabasesData {
  generatedAt: string;
  clusters: Signal<DbCluster[]>;
  sources: Record<string, Signal<{ count: number }>>;
}

export function clusterKey(cluster: Pick<DbCluster, 'engine' | 'namespace' | 'name'>): string {
  return `${cluster.engine}/${cluster.namespace}/${cluster.name}`;
}

const BACKUP_REASON_TONE: Record<string, 'good' | 'bad' | 'warn'> = {
  ok: 'good',
  stuck_backup_present: 'bad',
  no_backup_objects: 'bad',
  all_stale: 'bad',
  no_successful_backup: 'bad',
};

/* ------------------------------------------------------------------ *
 * Role chip
 * ------------------------------------------------------------------ */

/**
 * A database role.
 *
 * `unknown-role` is NOT a missing chip. The API emits it when the cluster
 * reports no primary, which is an incident-shaped fact — a Postgres cluster
 * with no primary is down — so it gets the dotted container and the dashed-ring
 * icon, exactly like every other "we do not know".
 */
function RoleChip({ role }: { role: string | null }): ReactNode {
  if (role === null) {
    return (
      <span className="epi-unmeasured rounded border px-1.5 text-[10px] font-medium text-ink-3">
        no role
      </span>
    );
  }
  // Ink, not hue. `primary` is the one you hunt for in a failover and gets the
  // filled container at full strength; `replica` the same container dimmer;
  // `unknown-role` the dotted one, because it is a "we do not know".
  const style =
    role === 'primary'
      ? 'epi-measured text-ink'
      : role === 'replica'
        ? 'epi-measured text-ink-3'
        : 'epi-unmeasured text-ink-3';
  return (
    <span className={`whitespace-nowrap rounded border px-1.5 text-[10px] font-medium uppercase tracking-wide ${style}`}>
      {role === 'unknown-role' ? 'unknown role' : role}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Placement map
 * ------------------------------------------------------------------ */

interface Placed {
  node: string | null;
  instances: { cluster: DbCluster; instance: DbInstance }[];
}

function placementOf(clusters: DbCluster[]): Placed[] {
  const byNode = new Map<string, { cluster: DbCluster; instance: DbInstance }[]>();
  const unknown: { cluster: DbCluster; instance: DbInstance }[] = [];
  for (const cluster of clusters) {
    for (const instance of cluster.instances) {
      if (instance.node === null || instance.node === '') {
        unknown.push({ cluster, instance });
        continue;
      }
      const list = byNode.get(instance.node) ?? [];
      list.push({ cluster, instance });
      byNode.set(instance.node, list);
    }
  }
  const placed: Placed[] = [...byNode.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([node, instances]) => ({ node, instances }));
  if (unknown.length > 0) placed.push({ node: null, instances: unknown });
  return placed;
}

function PlacementMap({
  clusters,
  navigate,
}: {
  clusters: DbCluster[];
  navigate: (to: string) => void;
}): ReactNode {
  const placed = placementOf(clusters);
  const total = placed.reduce((sum, group) => sum + group.instances.length, 0);

  return (
    <Card
      title="Placement"
      icon="placement"
      subtitle="if that node is gone, what did I lose?"
      right={<StatusPill status="ok">{`${String(total)} instances`}</StatusPill>}
      padding="flush"
    >
      <ul className="divide-y divide-line border-t border-line">
        {placed.map((group) => {
          const primaries = group.instances.filter((i) => i.instance.role === 'primary').length;
          return (
            <li key={group.node ?? '__unknown__'} className="px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                {group.node === null ? (
                  <Absent title="these instances exist and the API did not report a node for them — that is not the same as unscheduled, and it is not a placement of zero">
                    node not reported
                  </Absent>
                ) : (
                  <Ident className="text-sm font-medium text-ink">{group.node}</Ident>
                )}
                <p className="num text-2xs text-ink-3">
                  {group.instances.length} {group.instances.length === 1 ? 'instance' : 'instances'}
                  {primaries > 0 && (
                    <>
                      {' · '}
                      <span className="font-medium text-ink-2">{primaries} primary</span>
                    </>
                  )}
                </p>
              </div>

              <ul className="mt-2 flex flex-wrap gap-1.5">
                {group.instances.map(({ cluster, instance }) => (
                  <li key={`${clusterKey(cluster)}/${instance.name}`}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/databases/${clusterKey(cluster)}`);
                      }}
                      className="flex max-w-full items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-2 py-1 text-left hover:border-line-strong"
                    >
                      <RoleChip role={instance.role} />
                      <span className="mono min-w-0 truncate text-2xs text-ink-2">
                        {instance.name}
                      </span>
                      <span className="shrink-0 text-[10px] text-ink-3">{cluster.engine}</span>
                    </button>
                  </li>
                ))}
              </ul>


            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Index page
 * ------------------------------------------------------------------ */

export function DatabasesPage({
  data,
  navigate,
}: {
  data: DatabasesData | null;
  navigate: (to: string) => void;
}): ReactNode {
  if (data === null) {
    return (
      <Skeleton label="the cluster map" shapes={['h-56', 'h-56']} />
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <SignalPanel signal={data.clusters} emptyTitle="the cluster map could not be read">
        {(clusters) => (
          <div className="space-y-3 md:space-y-4">
            <PlacementMap clusters={clusters} navigate={navigate} />

            <Card title="Clusters" icon="databases" padding="flush">
              <ul className="divide-y divide-line border-t border-line">
                {clusters.map((cluster) => (
                  <li key={clusterKey(cluster)}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/databases/${clusterKey(cluster)}`);
                      }}
                      className="block w-full px-4 py-3 text-left transition-colors hover:bg-surface-2"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="flex items-baseline gap-2">
                            <span className="text-2xs uppercase tracking-[0.08em] text-ink-3">
                              {cluster.engine}
                            </span>
                            <Ident className="truncate text-sm font-medium text-ink">
                              {cluster.namespace}/{cluster.name}
                            </Ident>
                          </p>
                          {cluster.outcomeReason !== null && (
                            <p className="mt-0.5 text-2xs text-ink-3">{cluster.outcomeReason}</p>
                          )}
                        </div>
                        <StatusPill status={cluster.outcome}>
                          {labelFor(cluster.outcome)}
                        </StatusPill>
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-ink-3">
                        <span className="flex items-center gap-1.5">
                          <span className="text-ink-3">instances</span>
                          <span className="num text-ink-2">{cluster.instances.length}</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="text-ink-3">primary</span>
                          {cluster.primary === null ? (
                            <Absent size="sm">none reported</Absent>
                          ) : (
                            <Ident>{cluster.primary}</Ident>
                          )}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="text-ink-3">backup</span>
                          <BackupInline signal={cluster.lastBackup} />
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </SignalPanel>

      <Card title="Sources" icon="snapshot" subtitle="what each collector read this cycle" padding="flush">
        <dl className="grid grid-cols-2 gap-px border-t border-line bg-line sm:grid-cols-3 lg:grid-cols-6">
          {Object.entries(data.sources).map(([source, signal]) => (
            <div key={source} className="bg-surface px-3 py-2.5">
              <dt className="text-2xs uppercase tracking-[0.07em] text-ink-3">{source}</dt>
              <dd className="mt-1 text-sm">
                <SignalValue
                  signal={signal}
                  format={(v) => `${formatNumber((v as { count: number }).count)} objects`}
                />
              </dd>
            </div>
          ))}
        </dl>
      </Card>

      <div className="grid gap-3 xl:grid-cols-2">
        <SeriesChart
          name="cnpg_replication_lag"
          title="CNPG replication lag"
          unit="seconds"
          defaultRange="6h"
        />
        <SeriesChart name="cnpg_backends" title="Postgres backends" defaultRange="6h" />
        <SeriesChart
          name="mariadb_replica_seconds_behind"
          title="MariaDB seconds behind master"
          unit="seconds"
          defaultRange="6h"
        />
        <SeriesChart name="proxysql_backend_status" title="ProxySQL backend state" defaultRange="6h" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Detail page
 * ------------------------------------------------------------------ */

/**
 * Charts worth drawing for each engine.
 *
 * The catalogue is closed and compiled into the backend, so this is a lookup
 * from the engine string to names that exist — never a constructed query.
 */
const ENGINE_SERIES: Record<string, { name: string; title: string; unit?: string }[]> = {
  cnpg: [
    { name: 'cnpg_replication_lag', title: 'Replication lag', unit: 'seconds' },
    { name: 'cnpg_backends', title: 'Backends' },
    { name: 'cnpg_archiver_failed', title: 'WAL archive failures' },
    { name: 'cnpg_deadlocks_1h', title: 'Deadlocks per hour' },
  ],
  mariadb: [
    { name: 'mariadb_replica_seconds_behind', title: 'Seconds behind master', unit: 'seconds' },
    { name: 'mariadb_gtid_divergence', title: 'GTID divergence' },
    { name: 'mysql_slave_seconds_behind', title: 'mysqld seconds behind', unit: 'seconds' },
  ],
  proxysql: [{ name: 'proxysql_backend_status', title: 'Backend state' }],
  maxscale: [{ name: 'mariadb_replica_topology', title: 'Replica topology' }],
  psmdb: [],
};

export function DatabaseDetailPage({
  data,
  clusterId,
  navigate,
}: {
  data: DatabasesData | null;
  clusterId: string;
  navigate: (to: string) => void;
}): ReactNode {
  if (data === null) {
    return <Skeleton label="this cluster" shapes={['h-64']} />;
  }

  const back = (
    <button
      type="button"
      onClick={() => {
        navigate('/databases');
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 text-xs text-ink-3 hover:border-line-strong hover:text-ink"
    >
      <span aria-hidden="true">←</span> all databases
    </button>
  );

  if (data.clusters.status !== 'ok' || data.clusters.value === undefined) {
    return (
      <div className="space-y-3">
        {back}
        <SignalPanel signal={data.clusters} emptyTitle="the cluster map could not be read">
          {() => null}
        </SignalPanel>
      </div>
    );
  }

  const cluster = data.clusters.value.find((c) => clusterKey(c) === clusterId);
  if (cluster === undefined) {
    return (
      <div className="space-y-3">
        {back}
        <Placeholder status="empty" title="no such cluster in the current map">
          <span className="mono">{clusterId}</span> is not in the map this cycle. It may have been
          renamed, or the collector that reports it may have failed — check the Sources panel on the
          index.
        </Placeholder>
      </div>
    );
  }

  const charts = ENGINE_SERIES[cluster.engine] ?? [];

  return (
    <div className="space-y-3 md:space-y-4">
      {back}

      <Card
        title={
          <span className="flex items-baseline gap-2">
            <span className="text-2xs uppercase tracking-[0.08em] text-ink-3">{cluster.engine}</span>
            <span>
              {cluster.namespace}/{cluster.name}
            </span>
          </span>
        }
        subtitle={cluster.outcomeReason ?? undefined}
        right={<StatusPill status={cluster.outcome}>{labelFor(cluster.outcome)}</StatusPill>}
        padding="flush"
      >
        {cluster.instances.length === 0 ? (
          <div className="px-4 pb-4">
            <Placeholder status="empty" title="no instances reported for this cluster">
              The cluster object exists and lists no instances.
            </Placeholder>
          </div>
        ) : (
          <>
            {/*
              A card per instance below `lg`. The seven-column table needs
              ~750 px; at 390 px it scrolled sideways and put lag, backends and
              restarts — the three numbers you came for during a failover —
              behind an undiscoverable horizontal gesture.
            */}
            <ul className="divide-y divide-line border-t border-line lg:hidden">
              {cluster.instances.map((instance) => (
                <li key={instance.name} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <Ident className="min-w-0 break-all text-sm leading-snug text-ink">
                      {instance.name}
                    </Ident>
                    <span className="shrink-0">
                      {instance.ready === null ? (
                        <Absent size="sm">ready unknown</Absent>
                      ) : (
                        <StatusPill status={instance.ready ? 'ok' : 'error'} size="sm">
                          {instance.ready ? 'ready' : 'not ready'}
                        </StatusPill>
                      )}
                    </span>
                  </div>

                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <RoleChip role={instance.role} />
                    {instance.node === null ? (
                      <Absent size="sm">node not reported</Absent>
                    ) : (
                      <Ident className="text-[10px] text-ink-3">{instance.node}</Ident>
                    )}
                  </div>

                  <dl className="mt-2 grid grid-cols-3 gap-x-3 text-2xs">
                    <div>
                      <dt className="text-ink-3">lag</dt>
                      <dd className="mt-0.5">
                        <SignalValue
                          signal={instance.lagSeconds}
                          format={(v) => `${formatNumber(v as number, 2)} s`}
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-ink-3">backends</dt>
                      <dd className="mt-0.5">
                        <SignalValue
                          signal={instance.backends}
                          format={(v) => formatNumber(v as number, 0)}
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-ink-3">restarts</dt>
                      <dd className="num mt-0.5">
                        {instance.restarts == null ? (
                          <span className="text-ink-3">not reported</span>
                        ) : (
                          <span className={instance.restarts > 0 ? 'text-warn' : 'text-ink-2'}>
                            {instance.restarts}
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>

            <div className="hidden border-t border-line lg:block">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">
                Instances of {cluster.namespace}/{cluster.name}, with node, role, readiness,
                replication lag, backends and restarts.
              </caption>
              <thead className="bg-surface-2 text-2xs uppercase tracking-[0.07em] text-ink-3">
                <tr>
                  <th scope="col" className="px-4 py-2 font-medium">instance</th>
                  <th scope="col" className="px-3 py-2 font-medium">role</th>
                  <th scope="col" className="px-3 py-2 font-medium">node</th>
                  <th scope="col" className="px-3 py-2 font-medium">ready</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">lag</th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">backends</th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">restarts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {cluster.instances.map((instance) => (
                  <tr key={instance.name}>
                    <th scope="row" className="px-4 py-2.5 font-normal">
                      <Ident className="whitespace-nowrap">{instance.name}</Ident>
                    </th>
                    <td className="px-3 py-2.5">
                      <RoleChip role={instance.role} />
                    </td>
                    <td className="px-3 py-2.5">
                      {instance.node === null ? (
                        <Absent size="sm">not reported</Absent>
                      ) : (
                        <Ident className="whitespace-nowrap">{instance.node}</Ident>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {instance.ready === null ? (
                        <Absent size="sm">unknown</Absent>
                      ) : (
                        <StatusPill status={instance.ready ? 'ok' : 'error'} size="sm">
                          {instance.ready ? 'ready' : 'not ready'}
                        </StatusPill>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <SignalValue
                        signal={instance.lagSeconds}
                        format={(v) => `${formatNumber(v as number, 2)} s`}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <SignalValue
                        signal={instance.backends}
                        format={(v) => formatNumber(v as number, 0)}
                      />
                    </td>
                    <td className="num px-4 py-2.5 text-right">
                      {instance.restarts == null ? (
                        <span className="text-ink-3">not reported</span>
                      ) : (
                        <span className={instance.restarts > 0 ? 'text-warn' : 'text-ink-2'}>
                          {instance.restarts}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card title="Durability" subtitle="backups, WAL archiving, restore drill">
          <dl className="divide-y divide-line">
            <KeyValue label="WAL archive failures">
              <SignalValue
                signal={cluster.archiveFailures}
                format={(v) => formatNumber(v as number, 0)}
              />
            </KeyValue>
            <KeyValue label="last backup">
              {cluster.lastBackup.status === 'ok' && cluster.lastBackup.value !== undefined ? (
                <BackupCell value={cluster.lastBackup.value} />
              ) : (
                <SignalValue signal={cluster.lastBackup} />
              )}
            </KeyValue>
            <KeyValue label="restore drill">
              {cluster.restoreDrill.status === 'ok' ? (
                <span
                  className={
                    cluster.restoreDrill.value === 1 ? 'font-medium text-ink' : 'font-medium text-bad'
                  }
                >
                  {cluster.restoreDrill.value === 1 ? 'passed' : 'FAILED'}
                </span>
              ) : (
                <SignalValue signal={cluster.restoreDrill} />
              )}
            </KeyValue>
          </dl>
        </Card>

        <Card title="Backends" subtitle="MaxScale / ProxySQL server states">
          {cluster.backends.status === 'ok' && cluster.backends.value !== undefined ? (
            cluster.backends.value.length === 0 ? (
              <Placeholder status="empty" title="no backends reported" />
            ) : (
              <ul className="space-y-1.5">
                {cluster.backends.value.map((backend) => (
                  <li
                    key={backend.name}
                    className="flex items-center justify-between gap-3 rounded-lg bg-surface-2 px-2.5 py-2"
                  >
                    <Ident className="min-w-0 truncate">{backend.name}</Ident>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-2xs text-ink-3">{backend.state}</span>
                      <StatusPill status={backend.health === 'down' ? 'error' : 'ok'} size="sm">
                        {backend.health}
                      </StatusPill>
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <SignalPanel signal={cluster.backends} emptyTitle="backend states unavailable">
              {() => null}
            </SignalPanel>
          )}
        </Card>
      </div>

      {cluster.notes.length > 0 && (
        <Card title="Notes" subtitle="what the collector recorded about this cluster this cycle">
          <ul className="space-y-1.5">
            {cluster.notes.map((note, index) => (
              <li key={index} className="text-xs leading-relaxed text-ink-2">
                {note}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/*
        PLAN §7 also asks for a Performance section here — top digests, seq-scan
        tables, unused/duplicate indexes, lock waits and the dbperf proposals for
        this cluster. None of that is on the wire: `/api/databases` returns the
        shape in `api/types.ts#DbCluster` and there is no per-cluster endpoint.
        This panel says so rather than leaving a silent hole where an operator
        would reasonably assume "nothing to report".
      */}
      <Card title="Performance" subtitle="digests, seq scans, unused indexes, lock waits">
        <Placeholder status="unproven" title="no per-cluster performance endpoint">
          The fields are in PLAN §7, not on the wire. An absence of API surface, not a reading of
          zero.
        </Placeholder>
      </Card>

      {charts.length > 0 && (
        <div className="grid gap-3 xl:grid-cols-2">
          {charts.map((chart) => (
            <SeriesChart
              key={chart.name}
              name={chart.name}
              title={chart.title}
              defaultRange="6h"
              {...(chart.unit === undefined ? {} : { unit: chart.unit })}
            />
          ))}
        </div>
      )}
      {charts.length > 0 && (
        <p className="text-center text-2xs leading-relaxed text-ink-3">
          These charts are cluster-wide for the {cluster.engine} engine — the series catalogue has no
          per-cluster filter, so they include every {cluster.engine} cluster in the estate.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Backup rendering
 * ------------------------------------------------------------------ */

function BackupInline({
  signal,
}: {
  signal: Signal<{ at: string | null; ageHours: number | null; reason: string }>;
}): ReactNode {
  if (signal.status !== 'ok' || signal.value === undefined) {
    return <SignalValue signal={signal} />;
  }
  const value = signal.value;
  const tone = BACKUP_REASON_TONE[value.reason] ?? 'warn';
  if (value.ageHours === null) {
    return (
      <span className={tone === 'bad' ? 'font-medium text-bad' : 'font-medium text-warn'}>
        {value.reason.replace(/_/g, ' ')}
      </span>
    );
  }
  return (
    <span className="num text-ink-2" title={value.at ?? undefined}>
      {humanizeSeconds(value.ageHours * 3600)} old
    </span>
  );
}

function BackupCell({
  value,
}: {
  value: { at: string | null; ageHours: number | null; reason: string };
}): ReactNode {
  const tone = BACKUP_REASON_TONE[value.reason] ?? 'warn';
  return (
    <span className="inline-flex flex-wrap items-baseline justify-end gap-x-2">
      <span className="num">
        {value.ageHours === null ? (
          'no successful backup'
        ) : (
          <span title={value.at ?? undefined}>
            {humanizeSeconds(value.ageHours * 3600)} old
            {value.at !== null && (
              <span className="ml-1 text-2xs text-ink-3">({relativeTime(value.at)})</span>
            )}
          </span>
        )}
      </span>
      <span
        className={`text-2xs font-medium ${
          tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : 'text-ink-3'
        }`}
      >
        {value.reason.replace(/_/g, ' ')}
      </span>
    </span>
  );
}

export { Eyebrow };
